<template>
  <div class="flex flex-col gap-3">
    <div class="flex flex-wrap items-center justify-between gap-4">
      <p class="text-sm text-gray-500">
        <template v-if="!loading && !error">
          <b class="text-gray-900">{{ totalItems }}</b> items across <b class="text-gray-900">{{ agenda.length }}</b> practices
          · <b :class="overdueCount ? 'text-red-dark' : 'text-gray-900'">{{ overdueCount }}</b> overdue
          · <b :class="noPlanCount ? 'text-red-dark' : 'text-gray-900'">{{ noPlanCount }}</b> problems with no plan
        </template>
      </p>
      <FilterTabs v-model="scope" :options="scopeOptions" />
    </div>

    <div v-if="error" class="h-[500px] overflow-y-auto rounded-lg border border-red-light bg-red-tint p-4 text-sm text-red-dark">
      {{ error }}
      <button type="button" class="btn-link ml-2" @click="load">Retry</button>
    </div>

    <div v-else-if="loading" class="h-[500px] overflow-hidden rounded-lg border border-gray-200">
      <div v-for="n in 8" :key="n" class="flex items-start gap-3 border-b border-growth-50 px-4 py-3 animate-pulse">
        <div class="mt-0.5 h-4 w-4 rounded bg-gray-100"></div>
        <div class="h-4 w-20 rounded bg-gray-100"></div>
        <div class="flex-1 space-y-2">
          <div class="h-3 w-2/3 rounded bg-gray-100"></div>
          <div class="h-2.5 w-1/3 rounded bg-gray-100"></div>
        </div>
      </div>
    </div>

    <div v-else class="h-[500px] min-h-0 overflow-auto rounded-lg border border-gray-200">
      <table class="w-full table-fixed border-collapse text-sm">
        <colgroup>
          <col class="w-[5%]" />
          <col class="w-[13%]" />
          <col class="w-[56%]" />
          <col class="w-[14%]" />
          <col class="w-[12%]" />
        </colgroup>
        <tbody>
          <template v-for="p in agenda" :key="p.id">
            <tr class="sticky top-0 z-10 border-b border-growth-50 bg-growth-10">
              <td colspan="5" class="px-4 py-2">
                <div class="flex flex-wrap items-center gap-3">
                  <router-link :to="`/entity/${p.id}`" class="sensitive font-semibold text-growth-900 hover:underline">{{ p.name }}</router-link>
                  <span class="inline-flex whitespace-nowrap rounded px-1.5 py-0.5 text-[11px] font-semibold" :class="dueClass(p)">{{ dueLabel(p) }}</span>
                  <span class="text-xs text-growth-600">Last met {{ ago(p.last_meeting) }}</span>
                  <span class="ml-auto text-xs text-growth-600">
                    <b class="text-growth-900">{{ p.items.length }}</b> to cover<template v-if="p.overdue"> · <b class="text-red-dark">{{ p.overdue }}</b> overdue</template>
                  </span>
                </div>
              </td>
            </tr>
            <tr
              v-for="item in visibleItems(p)"
              :key="item.id"
              class="border-b border-growth-50 align-top"
              :class="[item.flag ? 'border-l-2 border-l-primary-500' : 'border-l-2 border-l-transparent', busy[item.id] ? 'opacity-50' : '']"
            >
              <td class="py-2.5 pl-4 pr-1">
                <input
                  type="checkbox"
                  class="mt-0.5 h-4 w-4 accent-neutral-900"
                  :disabled="busy[item.id]"
                  :title="item.kind === 'constraint' ? 'Mark resolved' : 'Mark done'"
                  @change="resolve(item)"
                />
              </td>
              <td class="px-3 py-2.5">
                <span class="inline-flex whitespace-nowrap rounded px-1.5 py-0.5 text-[11px] font-semibold" :class="item.verbClass">{{ item.verb }}</span>
              </td>
              <td class="px-3 py-2.5">
                <div class="sensitive break-words font-semibold leading-5 text-growth-900">{{ item.title }}</div>
                <div class="mt-1 flex flex-wrap items-center gap-2 text-xs text-growth-600">
                  <span v-if="item.severity === 'high'" class="inline-flex rounded bg-red-tint px-1.5 py-0.5 text-[11px] font-semibold text-red-dark">High severity</span>
                  <span v-if="item.overdue" class="inline-flex rounded bg-red-tint px-1.5 py-0.5 text-[11px] font-semibold text-red-dark">Overdue · due {{ fmt(item.due_date) }}</span>
                  <span v-else-if="item.due_date">Due {{ fmt(item.due_date) }}</span>
                  <span v-if="item.owner && item.owner_side !== 'me'" class="sensitive">{{ item.owner }}</span>
                  <span v-if="item.initiative">{{ initiativeLabel(item.initiative) }}</span>
                  <span v-if="item.addresses">↳ <span>{{ item.addresses }}</span></span>
                  <span v-if="item.dups > 1" class="inline-flex rounded bg-growth-25 px-1.5 py-0.5 text-[11px] font-semibold text-gray-700">×{{ item.dups }} similar</span>
                </div>
              </td>
              <td class="px-3 py-2.5 text-xs text-growth-600">
                Last discussed
                <div class="text-sm text-growth-800">{{ ago(item.last_seen) }}</div>
              </td>
              <td class="whitespace-nowrap py-1.5 pr-2 text-right">
                <button type="button" class="btn-icon" :disabled="busy[item.id]" title="No longer relevant — drop" @click="drop(item)">
                  <font-awesome-icon :icon="['fas', 'xmark']" />
                </button>
              </td>
            </tr>
            <tr v-if="p.items.length > perPractice" class="border-b border-growth-50">
              <td colspan="5" class="py-2 text-center">
                <button type="button" class="btn-link text-xs" @click="expandedPractices[p.id] = !expandedPractices[p.id]">
                  {{ expandedPractices[p.id] ? 'Show fewer' : `Show all ${p.items.length}` }}
                </button>
              </td>
            </tr>
          </template>
          <tr v-if="!agenda.length">
            <td colspan="5" class="px-5 py-6 text-sm text-growth-600">{{ emptyMessage }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <p v-if="actionError" class="text-xs text-red-dark">{{ actionError }}</p>
  </div>
</template>

<script>
import FilterTabs from '@/components/widgetcomponents/library/FilterTabs.vue'
import { getNextMeetings, completeAction, dropAction, setConstraintStatus } from '@/services/api/engagementGraph'

const PER_PRACTICE = 6
const INITIATIVE_LABELS = {
  awv_completion: 'AWV',
  visit_coverage: 'Visit coverage',
  risk_documentation: 'Risk documentation',
  transitions_of_care: 'Transitions of care',
  contract_payments: 'Contract & payments',
}
const VERBS = {
  plan: { verb: 'Agree a plan', verbClass: 'bg-primary-100 text-primary-800' },
  progress: { verb: 'Check progress', verbClass: 'bg-primary-50 text-primary-800' },
  me: { verb: 'Report back', verbClass: 'bg-neutral-50 text-neutral-900' },
  team: { verb: 'Update them', verbClass: 'bg-growth-25 text-gray-700' },
  practice: { verb: 'Chase', verbClass: 'bg-secondary-100 text-secondary-900' },
  unassigned: { verb: 'Follow up', verbClass: 'bg-growth-25 text-gray-700' },
}

export default {
  name: 'NextMeetings_List',
  components: { FilterTabs },
  props: {
    entity_id: { type: Number, default: null },
    sectionId: { type: String, default: null },
    expanded: { type: Boolean, default: true },
    fullWidth: { type: Boolean, default: false },
  },
  data() {
    return {
      practices: [],
      loading: true,
      error: null,
      actionError: null,
      scope: 'week',
      scopeOptions: [
        { value: 'week', label: 'Due this week' },
        { value: 'month', label: 'Next 30 days' },
        { value: 'all', label: 'All practices' },
      ],
      perPractice: PER_PRACTICE,
      expandedPractices: {},
      busy: {},
    }
  },
  computed: {
    agenda() {
      return this.practices
        .filter(p => this.inScope(p))
        .map(p => {
          const items = this.dedupe(p.items).map(this.decorate)
          return { ...p, items, overdue: items.filter(i => i.overdue).length }
        })
        .filter(p => p.items.length)
    },
    totalItems() {
      return this.agenda.reduce((n, p) => n + p.items.length, 0)
    },
    overdueCount() {
      return this.agenda.reduce((n, p) => n + p.overdue, 0)
    },
    noPlanCount() {
      return this.agenda.reduce((n, p) => n + p.items.filter(i => i.kind === 'constraint' && !i.covered).length, 0)
    },
    emptyMessage() {
      if (!this.entity_id) return 'No linked entity for this user.'
      if (this.practices.length) return 'Nothing due in this window.'
      return 'No open gaps or action items at the practices you manage.'
    },
  },
  watch: {
    entity_id() {
      this.load()
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    async load() {
      if (!this.entity_id) {
        this.practices = []
        this.loading = false
        return
      }
      this.loading = true
      this.error = null
      const requested = this.entity_id
      try {
        const data = await getNextMeetings(requested)
        if (requested !== this.entity_id) return
        this.practices = data.practices
      } catch (e) {
        this.error = e?.response?.data?.detail || 'Could not load your next meetings.'
      } finally {
        this.loading = false
      }
    },
    inScope(p) {
      if (this.scope === 'all') return true
      if (p.lapsed || p.days_until_due === null) return false
      return p.days_until_due <= (this.scope === 'week' ? 7 : 30)
    },
    dedupe(items) {
      const seen = new Map()
      for (const item of items) {
        if (item.kind !== 'action') {
          seen.set(`c:${item.id}`, { ...item, dups: 1 })
          continue
        }
        const key = `a:${item.owner_side}:${(item.title || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()}`
        const current = seen.get(key)
        if (current) current.dups += 1
        else seen.set(key, { ...item, dups: 1 })
      }
      return [...seen.values()]
    },
    decorate(item) {
      const key = item.kind === 'constraint' ? (item.covered ? 'progress' : 'plan') : item.owner_side
      const noPlan = item.kind === 'constraint' && !item.covered
      return { ...item, ...VERBS[key], flag: item.overdue || noPlan }
    },
    visibleItems(p) {
      return this.expandedPractices[p.id] ? p.items : p.items.slice(0, this.perPractice)
    },
    async resolve(item) {
      await this.update(item, () => (item.kind === 'constraint' ? setConstraintStatus(item.id, 'resolved') : completeAction(item.id)))
    },
    async drop(item) {
      await this.update(item, () => (item.kind === 'constraint' ? setConstraintStatus(item.id, 'dropped') : dropAction(item.id)))
    },
    async update(item, request) {
      this.busy[item.id] = true
      this.actionError = null
      try {
        await request()
        this.practices = this.practices.map(p => ({ ...p, items: p.items.filter(i => i.id !== item.id) }))
      } catch (e) {
        this.actionError = e?.response?.data?.detail || 'That change could not be saved.'
      } finally {
        delete this.busy[item.id]
      }
    },
    dueLabel(p) {
      if (p.lapsed) return 'Lapsed'
      if (p.next_scheduled) return `Scheduled ${this.fmt(p.next_scheduled)}`
      if (p.days_until_due === null) return 'No meetings yet'
      if (p.days_until_due < 0) return `Due ${-p.days_until_due}d ago`
      if (p.days_until_due === 0) return 'Due today'
      if (p.days_until_due <= 7) return `Due in ${p.days_until_due}d`
      return `Due ${this.fmt(p.next_due)}`
    },
    dueClass(p) {
      if (p.lapsed || p.days_until_due === null) return 'bg-growth-25 text-gray-700'
      if (p.days_until_due <= 0) return 'bg-primary-100 text-primary-800'
      if (p.days_until_due <= 7) return 'bg-neutral-50 text-neutral-900'
      return 'bg-growth-25 text-gray-700'
    },
    initiativeLabel(key) {
      return INITIATIVE_LABELS[key] || key
    },
    fmt(d) {
      return d ? new Date(`${d}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' }) : '—'
    },
    ago(d) {
      if (!d) return '—'
      const days = Math.round((Date.now() - new Date(`${d}T00:00:00`).getTime()) / 864e5)
      if (days <= 0) return 'Today'
      if (days === 1) return 'Yesterday'
      if (days < 45) return `${days} days ago`
      if (days < 365) return `${Math.round(days / 30)} months ago`
      return `${(days / 365).toFixed(1)} years ago`
    },
  },
}
</script>
